export type CustomScriptType = 'dashboard' | 'registration' | 'login';
export type ScriptPosition = 'head' | 'body';
export type ScriptInjection = {
  [key in ScriptPosition]?: string;
};

const SCRIPT_ATTRS = ['type', 'src', 'async', 'defer', 'charset'];

const buildScriptElement = (currentScript: HTMLScriptElement, nonce?: string) => {
  const script = document.createElement('script');

  // Copy attributes
  for (let i = 0; i < SCRIPT_ATTRS.length; i++) {
    const attr = SCRIPT_ATTRS[i];
    const currentAttrValue = currentScript.getAttribute(attr);
    if (currentAttrValue) {
      script.setAttribute(attr, currentAttrValue);
    }

    // Add nonce if provided
    if (nonce) {
      script.setAttribute('nonce', nonce);
    }

    // Copy inner content
    script.innerHTML = currentScript.innerHTML;
  }

  return script;
};

export const getCustomScriptType = (path: string): CustomScriptType | null => {
  if (/login|logout|forgot|set-password|unsubscribe/.test(path)) return 'login';
  if (path.includes('register')) return 'registration';
  if (/dashboard|app/.test(path)) return 'dashboard';
  return null;
};

export const buildScriptElements = (dataId: string, script: string, nonce?: string): HTMLScriptElement[] => {
  const el = document.createElement('div');

  el.innerHTML = script;

  // Check if the string contains a script tag
  const scriptTags = el.querySelectorAll('script');
  if (scriptTags.length > 0) {
    const scripts = Array.from(scriptTags).map((scriptTag) => {
      const newScript = buildScriptElement(scriptTag, nonce);
      newScript.setAttribute(dataId, 'true');
      return newScript;
    });

    return scripts;
  }

  // If no script tag found, treat the string as a plain script
  const plainScriptEl = document.createElement('script');

  // Add the nonce attribute if provided
  if (nonce) {
    plainScriptEl.setAttribute('nonce', nonce);
  }

  plainScriptEl.innerHTML = script;
  plainScriptEl.setAttribute(dataId, 'true');
  return [plainScriptEl];
};

type ScriptConfig = {
  id: string;
  src: string;
  nonce?: string;
  onLoad: () => void;
  onError: () => void;
};

export function findExistingScript(id: string) {
  return document.getElementById(id) as HTMLScriptElement | null;
}

export function attachScript({ id, src, nonce, onLoad, onError }: ScriptConfig) {
  let script = findExistingScript(id);

  const handleLoad = () => {
    script?.removeEventListener('load', handleLoad);
    script?.removeEventListener('error', handleError);
    onLoad();
  };

  const handleError = () => {
    script?.removeEventListener('load', handleLoad);
    script?.removeEventListener('error', handleError);
    onError();
  };

  if (!script) {
    script = document.createElement('script');
    script.id = id;
    script.async = true;
    script.src = src;
    if (nonce) script.nonce = nonce;
    script.addEventListener('load', handleLoad);
    script.addEventListener('error', handleError);
    document.head.appendChild(script);
  }

  return {
    script,
    cleanup: () => {
      script?.removeEventListener('load', handleLoad);
      script?.removeEventListener('error', handleError);
    },
  };
}
